import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { isoFormatDate } from '@shared/utils/functions/format-date';
import { AbstractPanelManagementDataSource, IPaginatorModel } from '@types-custom/models/ui/paginator-model';
import { ISdkDynamicFilterModel } from '@types-custom/models/ui/dynamic-filter';
import { ILocationOption, ILocationPoint } from '@types-custom/models/business/location.model';
import { notifationsAPIMapper } from '@types-custom/models/ui/notifications-panel-model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService
  implements
  AbstractPanelManagementDataSource<any> {

  public notificationStatus: BehaviorSubject<{
    updateDate: Date;
    notificationAmount: number;
  }> = new BehaviorSubject<{
    updateDate: Date;
    notificationAmount: number;
  }>(null);

  private dataSubject = new BehaviorSubject<any[]>([]);
  data$ = this.dataSubject.asObservable();
  pageInfo = new BehaviorSubject<IPaginatorModel>({ page: 0, pageSize: 10 });
  selectedData!: BehaviorSubject<any | ILocationPoint<string> | undefined>;
  selected$ = new BehaviorSubject<any | undefined>(undefined);
  formFiltersValue?: any;
  states$ = new BehaviorSubject<ILocationOption[] | undefined>(undefined);
  statesSelected$ = new BehaviorSubject<string | undefined>(undefined);

  dataMap: { [key: number]: any } = {
    0: (): string => `pathState`,
    3: (): string => `pathAgencias`,
  };

  submitCreateForm: (formValue: any) => Observable<any>;
  submitEditForm: (formValue: any) => Observable<any>;
  searchFromFilters: (formFiltersValue: any) => void;
  getSelectOptions: (optionType: string, value?: number) => Observable<any>;
  buildExcelPdfUrl: (formValue: any) => string;

  constructor(
    @Inject('environment') private environment: any,
    private httpClient: HttpClient
  ) { }

  init(): Observable<IPaginatorModel<any>> {
    this.setSelectedDataSubject(this.selected$);
    return this.pageInfo.asObservable();
  }

  updateReadNotifications(id: string): void {
    if (!id) {
      console.error('id is required');
      return;
    }
    this.httpClient.put(`${this.environment.alertUrl}/alerts-history/update-read/${id}`, {
      publicationDate: isoFormatDate(new Date()).fullDate,
    }, {}).subscribe();
  }

  resetNotificationStatus(): void {
    this.notificationStatus.next({
      updateDate: new Date(),
      notificationAmount: 0,
    });
  }

  increaseNotificationStatus(): void {
    this.notificationStatus.next({
      updateDate: new Date(),
      notificationAmount: this.notificationStatus.value.notificationAmount + 1,
    });
  }

  getNotificationStatus(): void {
    const date = this.getLastHours();
    this.httpClient.get(`${this.environment.alertUrl}/alerts-history/pagination/data`, {
      params: {
        beginDate: date,
        recordsPerPage: 1,
        page: 0,
      },
    }).subscribe((response: any) => {
      this.notificationStatus.next({
        updateDate: new Date(date),
        // notificationAmount: (response.totalRecords ?? response.totalCount ?? 0) - (response.recordsPerUser ?? 0),
        notificationAmount: response.recordsPerUser ?? 0,
      });
    });
  }

  fetchData(
    value: any,
    next: ISdkDynamicFilterModel<any, any>,
    index: number
  ): Observable<any> {
    const path = this.dataMap[index]?.();
    return this.httpClient.get(path);
  }

  fetchPageDataDistpacher(selectedData: any) {
    const dataObject: IPaginatorModel = {
      page: 0,
      pageSize: 10,
      data: selectedData,
    };
    this.fetchPageData(dataObject).subscribe();
  }

  setSelectedDataSubject(
    selectedData: BehaviorSubject<ILocationPoint<string> | undefined>
  ): void {
    this.selectedData = selectedData;
    this.selectedData.subscribe((selectedData) =>
      this.fetchPageDataDistpacher(selectedData)
    );
  }

  buildUrl(pageObject: IPaginatorModel): string {
    return `${this.environment.alertUrl}/alerts-history/pagination/data?beginDate=${this.getLastHours()}&recordsPerPage=${pageObject.pageSize}&page=${pageObject.page}`;
  }

  fetchPageData(pageInfo: IPaginatorModel<any>): Observable<IPaginatorModel> {
    pageInfo.data = this.selectedData.value;

    const url = this.buildUrl(pageInfo);
    return this.httpClient.get<any>(url).pipe(
      tap((response: any) => {
        const _pageInfo: IPaginatorModel = {
          page: response.currentPage,
          pageSize: pageInfo.pageSize,
          totalCount: response.totalRecords,
        };
        this.pageInfo.next(_pageInfo);
      }),
      tap((response: any) => {
        const data: any[] = response.records.map((el: any) => notifationsAPIMapper(el));
        this.dataSubject.next(data ?? []);
      })
    );
  }

  getLastHours(date: Date = new Date()): string {
    // const date = new Date();
    const timeCalc = 1000 * 60 * 60 * 24;
    const resultDate = date.getTime() - timeCalc;
    const dateFilter = new Date(resultDate);
    const objDate = isoFormatDate(dateFilter)
    return objDate.fullDate;
  }
}
